import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CurrentOrPreviousGroup, Member, School } from '../../../../../api/groups';
import { ScorableItem } from '../../api/scorable-items';
import { ResultModule, SchoolYearValue, useResultsDataContext } from '../../context/ResultsDataContext';
import { useSelectedGroups } from './hooks/useSelectedGroups';
import { GroupAndSelectedMemberId, useSelectedMembers } from './hooks/useSelectedMembers';
import { useSelectedModule } from './hooks/useSelectedModule';
import { useSelectedSchool } from './hooks/useSelectedSchool';
import { useSelectedSchoolYear } from './hooks/useSelectedSchoolYear';
import { Tab } from '../../../../../shared-components/elementis/types/ResultsPageTabs';
import { useSelectedScorableItems } from './hooks/useSelectedScorableItems';
import { useSearchParams } from 'react-router-dom';
import { FilterSearchParam } from '../../../../../shared-components/elementis/types/FilterSearchParam';
import ModuleContext from '../../../_contexts/ModuleContext';
import IdentityContext from '../../../../../contexts/IdentityContext';

export const FilterContext = createContext<
  | {
      selectedSchoolYear: SchoolYearValue | null;
      setSelectedSchoolYear: (schoolYear: SchoolYearValue) => void;
      selectedModule: ResultModule | null;
      setSelectedModule: (module: ResultModule) => void;
      selectedSchool: CurrentOrPreviousGroup['school'] | null;
      setSelectedSchool: (school: CurrentOrPreviousGroup['school']) => void;
      selectedGroups: CurrentOrPreviousGroup[];
      setSelectedGroups: (groups: CurrentOrPreviousGroup[]) => void;
      selectedMembers: Record<string, Member[]>;
      setSelectedMembers: (members: string[]) => void;
      selectedScorableItems: ScorableItem[];
      setSelectedScorableItems: (itemIds: string[]) => void;
      spScorableItemIds: string[]; // ids from search params
      spMemberIdsPerGroup: Record<string, GroupAndSelectedMemberId[]> | null; // ids from search params
      determiningFilters: boolean;
      clearFilters: () => void;
      usedLocalStorageFilters: boolean;
    }
  | undefined
>(undefined);

export const RESULT_FILTER_LOCAL_STORAGE_KEY = 'resultsFilterData';

function parseLocalStorageFilters(superModuleId: string, userId: string) {
  const filters = localStorage.getItem(`${RESULT_FILTER_LOCAL_STORAGE_KEY};${superModuleId};${userId}`);

  if (!filters) return null;

  try {
    const parsedFilters = JSON.parse(filters);
    return parsedFilters;
  } catch (error) {
    return null;
  }
}

function determineInitialFilters(
  activeTab: 'STUDENT' | 'EXERCISE',
  schoolYears: SchoolYearValue[],
  modules: ResultModule[],
  scorableItems: Record<string, Record<string, ScorableItem[]>>,
  groups: CurrentOrPreviousGroup[],
  selectedFilters?: {
    schoolYear: SchoolYearValue | null;
    module: ResultModule | null;
    groups: CurrentOrPreviousGroup[];
    school: School | null;
  },
) {
  let fallback;
  const filteredSchoolYears = schoolYears.filter(schoolYear =>
    selectedFilters?.schoolYear ? schoolYear.id === selectedFilters.schoolYear.id : true,
  );
  for (const schoolYear of filteredSchoolYears) {
    const filteredModules = modules.filter(module =>
      selectedFilters?.module ? module.id === selectedFilters.module.id : true,
    );

    for (const module of filteredModules) {
      const fallbackSchoolYearGroups = groups.filter(g => g.schoolYear === filteredSchoolYears[0].id);
      fallback = {
        schoolYear: filteredSchoolYears[0],
        module: filteredModules[0],
        school: fallbackSchoolYearGroups.length ? fallbackSchoolYearGroups[0].school : null,
        groups: fallbackSchoolYearGroups.length ? [fallbackSchoolYearGroups[0]] : [],
      };
      if (scorableItems[schoolYear.id]?.[module.id]?.length) {
        const groupsOfSchoolYear = groups
          .filter(group => {
            return group.schoolYear === schoolYear.id;
          })
          .filter(group => {
            return selectedFilters?.school ? selectedFilters.school.id === group.school.id : true;
          })
          .filter(group => {
            return selectedFilters?.groups.length ? selectedFilters.groups.some(g => g.id === group.id) : true;
          });

        if (groupsOfSchoolYear.length) {
          return {
            schoolYear,
            module,
            groups: activeTab === 'STUDENT' ? [groupsOfSchoolYear[0]] : groupsOfSchoolYear,
            school: groupsOfSchoolYear[0]?.school,
          };
        }
      }
    }
  }

  return fallback;
}

export function FilterContextProvider({ children, selectedTab }: { children: React.ReactNode; selectedTab: Tab }) {
  const { schoolYears, modules, groups, scorableItemsPerSchoolYearPerModule, loading } = useResultsDataContext();
  const { module } = useContext(ModuleContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedModule, setSelectedModule] = useSelectedModule();
  const [selectedSchoolYear, setSelectedSchoolYear] = useSelectedSchoolYear();
  const [selectedSchool, setSelectedSchool] = useSelectedSchool();
  const { identity } = useContext(IdentityContext);
  const [selectedGroups, setSelectedGroups] = useSelectedGroups({
    selectedTab,
    selectedSchoolYear,
    selectedSchool,
  });
  const { selectedMembers, setSelectedMembers, spMemberIdsPerGroup } = useSelectedMembers({
    selectedGroups,
    selectedTab,
    selectedSchool,
  });
  const { selectedScorableItems, setSelectedScorableItems, spScorableItemIds } = useSelectedScorableItems({
    selectedModule,
    selectedSchoolYear,
  });
  const [notEnoughData, setNotEnoughData] = useState(false);
  const [usedLocalStorageFilters, setUsedLocalStorageFilters] = useState(false);

  const setDefaultFilters = useCallback(() => {
    if (Object.values(loading).some(x => x)) return;
    if (selectedSchoolYear && selectedModule && selectedGroups.length && selectedSchool) return;

    const initialFilters = determineInitialFilters(
      selectedTab,
      [schoolYears!.current, schoolYears!.previous],
      modules,
      scorableItemsPerSchoolYearPerModule,
      groups,
      {
        schoolYear: selectedSchoolYear,
        module: selectedModule,
        groups: selectedGroups,
        school: selectedSchool,
      },
    );

    if (initialFilters) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          if (!selectedSchoolYear) newParams.set(FilterSearchParam.SchoolYear, initialFilters!.schoolYear.id);
          if (!selectedModule) newParams.set(FilterSearchParam.Module, initialFilters!.module.id);
          if (!selectedSchool && initialFilters.school)
            newParams.set(FilterSearchParam.School, initialFilters!.school.id);
          if ((!selectedGroups || selectedGroups.length === 0) && initialFilters.groups)
            newParams.set(FilterSearchParam.Groups, initialFilters!.groups.map(g => g.id).join(','));

          return newParams;
        },
        { replace: true },
      );

      if (!initialFilters.school || !initialFilters.groups) setNotEnoughData(true);
    }
  }, [
    schoolYears,
    modules,
    scorableItemsPerSchoolYearPerModule,
    groups,
    selectedTab,
    selectedSchoolYear,
    selectedModule,
    selectedGroups,
    selectedSchool,
    loading,
    setSearchParams,
  ]);

  function clearFilters() {
    localStorage.removeItem(`${RESULT_FILTER_LOCAL_STORAGE_KEY};${module?.id};${identity?.userId}`);

    setSearchParams(() => {
      const newParams = new URLSearchParams();

      return newParams;
    });
  }

  useEffect(() => {
    if (Object.values(loading).some(x => x)) return;

    const localStorageFilters = parseLocalStorageFilters(module?.id || '', identity?.userId || '');

    if (
      searchParams.get(FilterSearchParam.Module) ||
      searchParams.get(FilterSearchParam.School) ||
      searchParams.get(FilterSearchParam.SchoolYear)
    ) {
      return setDefaultFilters();
    }

    if (localStorageFilters) {
      setUsedLocalStorageFilters(true);
      return setSearchParams(new URLSearchParams(localStorageFilters), { replace: true });
    }

    setDefaultFilters();
  }, [loading, setDefaultFilters, module, searchParams, setSearchParams, identity.userId]);

  useEffect(() => {
    if (selectedSchoolYear && selectedModule && selectedSchool && module) {
      localStorage.setItem(
        `${RESULT_FILTER_LOCAL_STORAGE_KEY};${module.id};${identity.userId}`,
        JSON.stringify(Object.fromEntries(searchParams)),
      );
    }
  }, [selectedSchoolYear, selectedModule, selectedSchool, module, searchParams, identity.userId]);

  return (
    <FilterContext.Provider
      value={{
        selectedSchoolYear,
        setSelectedSchoolYear,
        selectedModule,
        setSelectedModule,
        selectedSchool,
        setSelectedSchool,
        selectedGroups,
        setSelectedGroups,
        selectedMembers,
        setSelectedMembers,
        selectedScorableItems,
        setSelectedScorableItems,
        spScorableItemIds,
        spMemberIdsPerGroup,
        determiningFilters:
          !notEnoughData && (!selectedSchoolYear || !selectedModule || !selectedGroups.length || !selectedSchool),
        clearFilters,
        usedLocalStorageFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error('useFilters must be used within a FilterContextProvider');
  }

  return context;
}
